import { useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash';
import Router from 'next/router';
import { memo, useCallback } from 'react';
import { useMount } from 'react-use';

import { useTrackViewedLogInForm } from '@air/analytics';
import { type Account } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { Modal, type ModalProps } from '@air/primitive-modal';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { getIsSAMLEnforced } from '@air/utils-auth';
import { cn } from '@air/utils-tailwind';

import { LoginForm } from '~/components/Auth/LoginForm';
import { DividerText } from '~/components/DividerText';
import { AirModalLogoHeader } from '~/components/Modals/AirModalLogoHeader';
import { SamlSSOLoginModal } from '~/components/Modals/SamlSSOLoginModal';
import { SignupModal, type SignupModalProps } from '~/components/Modals/SignupModal/SignUpModal';
import { type SSOCallbackData } from '~/components/PublicSSOCallback/utils';
import { SSOButtons } from '~/components/SSOButtons';
import { Routes } from '~/constants/routes';
import { getAccountKey } from '~/swr-hooks/account/useAccount';
import { useLogin } from '~/swr-hooks/auth/useLogin';

export type LoginModalProps = {
  onSuccess?: (account: Account) => void;
  ssoCallbackData?: SSOCallbackData;
  accountCreationSource: SignupModalProps['accountCreationSource'];
  /** Called when user dismisses modal */
  onCancel?: () => void;
  overlayClassName?: ModalProps['overlayClassName'];
};

const MODAL_OPEN_DELAY_MS = 200;

export const LoginModal = memo(
  ({
    onSuccess = noop,
    ssoCallbackData,
    onClose,
    onCancel,
    accountCreationSource,
    overlayClassName,
  }: AirModalProps<LoginModalProps>) => {
    const [showSignUpModal] = useAirModal(SignupModal);
    const [showSAMLSSOModal] = useAirModal(SamlSSOLoginModal);
    const [showLoginModal] = useAirModal(LoginModal);
    const queryClient = useQueryClient();
    const { showToast } = useToasts();
    const { trackViewedLogInForm } = useTrackViewedLogInForm();

    useMount(() => {
      Router.prefetch(Routes.auth.login);
      trackViewedLogInForm();
    });

    const { loginUser, showLoginError } = useLogin();

    const openSignupModal = useCallback(() => {
      onClose();
      showSignUpModal({ onSuccess, ssoCallbackData, accountCreationSource, onCancel, overlayClassName });
    }, [accountCreationSource, onCancel, onClose, onSuccess, overlayClassName, showSignUpModal, ssoCallbackData]);

    const openSAMLSSOModal = useCallback(() => {
      onClose();

      // add a short delay to avoid blinking when one modal is closing, and the second one appears
      setTimeout(
        () =>
          showSAMLSSOModal({
            onShowLoginClick: () => {
              showLoginModal({ onSuccess, ssoCallbackData, accountCreationSource });
            },
            ssoCallbackData,
            customState: {
              creationSource: accountCreationSource,
            },
          }),
        MODAL_OPEN_DELAY_MS,
      );
    }, [accountCreationSource, onClose, onSuccess, showLoginModal, showSAMLSSOModal, ssoCallbackData]);

    const onDismiss = useCallback(() => {
      onClose();
      onCancel?.();
    }, [onCancel, onClose]);

    const handleLoginUser = useCallback(
      async (username: string, password: string) => {
        const isSAMLEnforced = await getIsSAMLEnforced(username);

        if (isSAMLEnforced) {
          showToast('You must sign in with SAML SSO for this account', {
            timeBeforeAutoDismiss: undefined,
            type: 'assertive',
            withCloseButton: true,
          });
          return;
        }
        const account = await loginUser(username, password);
        await queryClient.invalidateQueries({ queryKey: getAccountKey() });
        onClose();
        onSuccess(account);
      },
      [loginUser, onClose, onSuccess, queryClient, showToast],
    );

    const handleLoginError = useCallback(
      (username: string, error: unknown) => {
        // this error comes from Cognito
        if (
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          error.code === 'PasswordResetRequiredException'
        ) {
          Router.push(Routes.auth.login);
        } else {
          showLoginError(username, error);
        }
      },
      [showLoginError],
    );

    return (
      <Modal
        data-testid="LOGIN_MODAL"
        // hide y scrollbar to avoid double scroll
        overlayClassName={cn(overlayClassName, 'overflow-y-hidden')}
        className="max-h-[80vh] w-[414px] overflow-y-auto p-8"
        isOpen
        onDismiss={onDismiss}
      >
        <AirModalLogoHeader onClose={onDismiss} />
        <h1 className="mb-4 text-center font-semibold ads:text-primary text-20 text-gray-12">Log in</h1>
        {ssoCallbackData && (
          <SSOButtons
            verbText="Log in"
            ssoCallbackData={ssoCallbackData}
            onContinueWithSamlClick={openSAMLSSOModal}
            customState={{
              creationSource: accountCreationSource,
            }}
          />
        )}
        <DividerText />
        <div className="flex flex-col gap-7">
          <LoginForm loginUser={handleLoginUser} onError={handleLoginError}>
            {({ isSubmitting, isValid }) => (
              <Button
                data-testid="LOGIN_MODAL_LOGIN_BUTTON"
                type="submit"
                className="mt-12 w-full"
                size="large"
                appearance="filled"
                color="blue"
                isLoading={isSubmitting}
                disabled={!isValid || isSubmitting}
              >
                Log in
              </Button>
            )}
          </LoginForm>
          <div className="flex flex-col items-center justify-center">
            <p className="text-center ads:text-secondary text-14 text-pigeon-600"> Don&apos;t have an account?</p>
            <button
              data-testid="OPEN_SIGNUP_MODAL_BUTTON"
              className="font-medium ads:text-accent text-14 text-jay-500 hover:underline"
              onClick={openSignupModal}
              type="button"
            >
              Sign up
            </button>
          </div>
        </div>
      </Modal>
    );
  },
);

LoginModal.displayName = 'LoginModal';
